import { useState, useEffect, useMemo } from 'react';
import s from './App.module.css';

const cx = (...v) => v.filter(Boolean).join(' ');
import CourseHome from './components/CourseHome.jsx';
import Syllabus from './components/Syllabus.jsx';
import LessonReader from './components/LessonReader.jsx';
import LessonNav from './components/LessonNav.jsx';
import { sectionsOf } from './lib/sections.js';
import Quiz from './components/Quiz.jsx';
import Exercise from './components/Exercise.jsx';
import Enrichment from './components/Enrichment.jsx';
import { course as baseCourse, getLesson } from './content/course.js';
import { useProgress, applyProgress } from './lib/progress.jsx';
import { unitOnDate } from './lib/term.js';
import { useTheme } from './lib/theme.jsx';
import { useTrack, TRACKS } from './lib/track.jsx';

/* A hash router. Hash rather than history because the site is deployed as
   static files (GitHub Pages) — a deep link must resolve without a server
   rewrite. URLs stay real and bookmarkable either way, which is the whole
   point of linking out instead of iframing. */
function useRoute() {
  const read = () => window.location.hash.replace(/^#\/?/, '') || '';
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const on = () => { setRoute(read()); window.scrollTo(0, 0); };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return [route, path => { window.location.hash = `#/${path}`; }];
}

/* Three states, three icons. A single cycling button showed only the current
   state and hid the other two, and sat in the nav as though "Light" were a
   destination. */
const THEME_ICON = {
  light: (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" fill="none"
         stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <circle cx="8" cy="8" r="3.1" />
      <path d="M8 1v1.6M8 13.4V15M15 8h-1.6M2.6 8H1M12.9 3.1l-1.1 1.1M4.2 11.8l-1.1 1.1M12.9 12.9l-1.1-1.1M4.2 4.2L3.1 3.1" />
    </svg>
  ),
  dark: (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" fill="none"
         stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
      <path d="M13.5 9.6A5.9 5.9 0 0 1 6.4 2.5a5.9 5.9 0 1 0 7.1 7.1Z" />
    </svg>
  ),
  system: (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true"
         stroke="currentColor" strokeWidth="1.4">
      <circle cx="8" cy="8" r="5.6" fill="none" />
      <path d="M8 2.4a5.6 5.6 0 0 1 0 11.2Z" fill="currentColor" stroke="none" />
    </svg>
  ),
};
const THEME_NAME = { light: 'Light', dark: 'Dark', system: 'Auto' };

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <div className={s.themes} role="group" aria-label="Colour theme">
      {['light', 'dark', 'system'].map(id => (
        <button key={id} type="button"
                className={cx(s.themeBtn, theme === id && s.themeOn)}
                onClick={() => setTheme(id)}
                aria-pressed={theme === id}
                title={THEME_NAME[id]} aria-label={`${THEME_NAME[id]} theme`}>
          {THEME_ICON[id]}
        </button>
      ))}
    </div>
  );
}

/* The language belongs to the course, not to the navigation. It reads as part
   of the title — "Programming Languages I · JavaScript" says what this course
   is for this student — rather than sitting among Course/Syllabus as though it
   were somewhere to go. */
function LanguageMark() {
  const [track, setTrack] = useTrack();
  if (!TRACKS.length) return null;
  if (TRACKS.length === 1) {
    return <span className={s.lang} title="Your language track">{TRACKS[0].label}</span>;
  }
  return (
    <select className={cx(s.lang, s.langPick)} value={track}
            onChange={e => setTrack(e.target.value)} aria-label="Your language track">
      {TRACKS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
    </select>
  );
}

function TopBar({ title, crumb, route, go }) {
  return (
    <div className={s.bar}>
      <div className={s.identity}>
        <button type="button" className={s.brand} onClick={() => go('')}>{title}</button>
        <span className={s.langSep} aria-hidden="true">·</span>
        <LanguageMark />
        {crumb && <><span className={s.sep} aria-hidden="true">/</span><span className={s.crumb}>{crumb}</span></>}
      </div>
      {/* Nav and theme travel together so a narrow screen wraps the whole right
          side as one unit, rather than dropping the icons under the title while
          the links stay above. */}
      <div className={s.tools}>
        <nav className={s.nav} aria-label="Course sections">
          <button type="button" className={s.navLink} onClick={() => go('')}
                  aria-current={route === '' ? 'page' : undefined}>Course</button>
          <button type="button" className={s.navLink} onClick={() => go('syllabus')}
                  aria-current={route === 'syllabus' ? 'page' : undefined}>Syllabus</button>
        </nav>
        <ThemeToggle />
      </div>
    </div>
  );
}

const Missing = ({ what }) => (
  <div className={s.missing}>
    <h1>No lesson “{what}”</h1>
    <p>Check the id in <code>content/cs140/course.yml</code>.</p>
    <p><a href="#/">← Back to the course</a></p>
  </div>
);

export default function App() {
  const [route, go] = useRoute();
  const { done } = useProgress();

  /* The course as the student sees it: designed starting state with their
     actual completions layered over the top. */
  /* With progress off there is no "where you are", so the highlighted module
     is the one running today, by its start date (D-058) — one at a time. */
  const { enabled: progressOn } = useProgress();
  const course = useMemo(() => {
    const units = applyProgress(baseCourse.units, done);
    if (progressOn) return { ...baseCourse, units };
    const now = unitOnDate(units);
    return { ...baseCourse, units: units.map(u => ({ ...u, status: u.id === now ? 'active' : u.status === 'done' ? 'done' : 'open' })) };
  }, [done, progressOn]);

  const all = useMemo(
    () => course.units.flatMap(u => u.lessons.map(l => ({ ...l, unit: u }))),
    [course]);

  const stats = useMemo(() => {
    const cleared = all.filter(l => l.status === 'done').length;
    return {
      total: all.length, cleared,
      pct: all.length ? Math.round((cleared / all.length) * 100) : 0,
      current: all.find(l => l.status === 'active') || all.find(l => l.status === 'next'),
    };
  }, [all]);

  const openLesson = l => go(`lesson/${l.id}`);
  const [, lessonId, sub] = route.split('/');
  const extra = sub === 'extra';

  let view, crumb = null, wide = false, full = false;
  /* The breadcrumb belongs to the lesson ROUTE, not to one view of it. It used
     to live inside LessonReader, which meant checks, trials and enrichment
     pages rendered with no way out except the browser's back button or a top
     nav link that dumped you at the course home. Hoisting it here means every
     lesson-scoped view gets it, and a new view kind cannot forget to. */
  let nav = null;

  if (route.startsWith('lesson/') && lessonId) {
    const i = all.findIndex(l => l.id === lessonId);
    const lesson = i >= 0 ? all[i] : null;
    const content = getLesson(lessonId);
    if (!lesson) {
      view = <Missing what={lessonId} />;
    } else {
      crumb = lesson.unit.meta;
      const prev = i > 0 ? all[i - 1] : null;
      const next = i < all.length - 1 ? all[i + 1] : null;
      /* Sections exist only for a prose lesson being read straight through.
         A quiz, a trial and an enrichment page pass none, and LessonNav omits
         the third segment rather than showing an empty menu. */
      const sections = !extra && lesson.kind === 'read' && content ? sectionsOf(content.body) : [];
      /* Match the breadcrumb's width to the view below it. The reader caps its
         acts at 1080; a quiz sits in the 1400 shell with 48px of padding; a
         trial takes 1560. Without this the bar is visibly inset on the wider
         two, because its cap was chosen for the reader. */
      const navStyle = lesson.kind === 'trial' ? { '--nav-max': '1560px', '--nav-pad': '24px' }
        : lesson.kind === 'check' ? { '--nav-max': '1400px', '--nav-pad': '48px' }
        : undefined;   /* read and enrichment both use the reader's acts */
      nav = <LessonNav lesson={lesson} unit={lesson.unit} units={course.units}
                       sections={sections} onOpen={openLesson}
                       onHome={() => go('')} style={navStyle} />;
      if (lesson.kind === 'check' && content) {
        view = <Quiz quiz={{ ...content, kicker: content.kicker || lesson.unit.meta }}
                     unlocks={content.unlocks}
                     onDone={() => next && openLesson(next)} />;
      } else if (lesson.kind === 'trial' && content) {
        /* three panes of editor; it uses every pixel it is given */
        wide = true;
        view = <Exercise exercise={{ ...content, title: lesson.title }} lessonId={lesson.id}
                         onPass={() => {}} />;
      } else if (extra) {
        crumb = 'GOING FURTHER';
        view = <Enrichment lesson={lesson} extra={content?.extra}
                           onBack={() => openLesson(lesson)} />;
      } else {
        full = true;
        view = <LessonReader lesson={lesson} unit={lesson.unit} content={content}
                             prev={prev} next={next} onOpen={openLesson}
                             onOpenExtra={l => go(`lesson/${l.id}/extra`)} />;
      }
    }
  } else if (route === 'syllabus') {
    crumb = 'SYLLABUS';
    view = <Syllabus course={course} stats={stats} onOpen={openLesson} />;
  } else {
    view = <CourseHome course={course} stats={stats} onOpen={openLesson}
                       onOpenSyllabus={() => go('syllabus')} />;
  }

  return (
    <div className={s.shell}>
      <TopBar title={course.title} crumb={crumb} route={route} go={go} />
      {nav}
      <div className={cx(s.main, wide && s.wide, full && s.full)}>{view}</div>
    </div>
  );
}
